import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

// TODO: this file should probably be in ui-lib, in the form folder

/**
 * Creates a mapped type to define form controls with the correct types
 * @example public form = new FormGroup<FormControls<AppCompanyEntity>>({...});
 */
export type FormControls<T> = {
  [K in keyof T]: T[K] extends string | number | boolean | null
    ? FormControl<T[K] | null> // Support both required and optional fields
    : FormControl<T[K] | null | undefined>;
};

export function checkControlValid<FormSchema>(
  form: FormGroup<FormControls<FormSchema>>,
  key: keyof FormSchema,
): boolean {
  return form && (form.controls[key].valid || !(form.controls[key].touched && form.controls[key].dirty));
}

export function checkSubControlValid<FormSchema>(control: FormControl<FormSchema>): boolean {
  return control.valid || !(control.touched && control.dirty);
}

export function hasSlugUnsafeCharacters(inputString: string): boolean {
  // Any characters other than a-z, 1-9, and dash (-)
  const invalidCharPattern = /[^a-z1-9-]/;
  return invalidCharPattern.test(inputString);
}

export function slugSafeCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const forbidden = hasSlugUnsafeCharacters(control.value);
    return forbidden ? { notSlugSafe: { value: control.value } } : null;
  };
}

/* Examples of allowed values:
15060583819
1506 05 83 819
1506.05.83.819
NO 1506 05 83 819
1506-05-83-819
 */
export function findInvalidBankAccountCharacter(inputString: string): string | null {
  // Any characters other than a-z, 0-9, dash (-), space ( ), dot (.)
  const invalidCharPattern = /[^a-z0-9\s-.]/i;
  const match = inputString.match(invalidCharPattern);
  return match ? match[0] : null;
}

export function bankAccountInvalidCharactersValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null; // No invalid characters
    }
    const invalidChar = findInvalidBankAccountCharacter(control.value);
    return invalidChar ? { invalidCharacters: { value: invalidChar } } : null;
  };
}

export function hasUppercaseChars(inputString: string): boolean {
  // Matches uppercase letters (A-Z)
  const uppercaseCharPattern = /[A-Z]|[ÅØÆ]/;
  return uppercaseCharPattern.test(inputString);
}

export function onlyLowerCaseValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const forbidden = hasUppercaseChars(control.value);
    return forbidden ? { mustBeLowerCase: { value: control.value } } : null;
  };
}

// TODO: move CheckOption, SelectOption etc. here
export type NkOption<T, Data = unknown> = Data extends unknown
  ? unknown extends Data
    ? NkOptionSimple<T> // Data is actually 'unknown'
    : NkOptionData<T, Data> // Data is set
  : never;

export interface NkOptionSimple<T> {
  value: T;
  label: string;
}

export interface NkOptionData<T, Data> {
  value: T;
  label: string;
  data: Data;
}
